import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Upload, Image as ImageIcon, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminBranding = () => {
  const [logo, setLogo] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/admin/branding`);
      setLogo(response.data.logo);
      setFavicon(response.data.favicon);
    } catch (error) {
      console.error('Failed to fetch branding');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('Logo darf maximal 2MB groß sein');
        return;
      }
      setLogoFile(file);
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 100 * 1024) { // 100KB limit
        toast.error('Favicon darf maximal 100KB groß sein');
        return;
      }
      setFaviconFile(file);
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFavicon(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    setUploading(true);
    try {
      const formData = new FormData();
      if (logoFile) formData.append('logo', logoFile);
      if (faviconFile) formData.append('favicon', faviconFile);

      await axios.post(`${API}/admin/branding`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Branding erfolgreich aktualisiert');
      setLogoFile(null);
      setFaviconFile(null);
      
      // Reload page to show new branding
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Brandings');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#7CD4FD' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
          Branding & Logo
        </h2>

        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>
              <ImageIcon className="w-6 h-6 inline-block mr-2" />
              Logo & Favicon hochladen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Logo Upload */}
            <div>
              <Label style={{ color: '#E6EDF3', fontSize: '16px', marginBottom: '12px', display: 'block' }}>
                Logo (empfohlen: 200x50px, PNG/SVG)
              </Label>
              
              {logo && (
                <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}>
                  <img 
                    src={logo} 
                    alt="Logo Preview" 
                    style={{ maxHeight: '80px', maxWidth: '300px' }}
                  />
                </div>
              )}

              <div className="flex gap-4">
                <label
                  className="flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition-colors"
                  style={{ 
                    backgroundColor: '#1F2937', 
                    border: '2px dashed #7CD4FD',
                    color: '#E6EDF3'
                  }}
                >
                  <Upload className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                  <span>Logo auswählen</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                </label>
                
                {logoFile && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg" style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>
                    <Check className="w-5 h-5" />
                    <span>{logoFile.name}</span>
                  </div>
                )}
              </div>
              <p className="text-sm mt-2" style={{ color: '#94A3B8' }}>
                Maximale Größe: 2MB. Unterstützte Formate: PNG, JPG, SVG
              </p>
            </div>

            {/* Favicon Upload */}
            <div>
              <Label style={{ color: '#E6EDF3', fontSize: '16px', marginBottom: '12px', display: 'block' }}>
                Favicon (empfohlen: 32x32px, ICO/PNG)
              </Label>
              
              {favicon && (
                <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}>
                  <img 
                    src={favicon} 
                    alt="Favicon Preview" 
                    style={{ width: '32px', height: '32px' }}
                  />
                </div>
              )}

              <div className="flex gap-4">
                <label
                  className="flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition-colors"
                  style={{ 
                    backgroundColor: '#1F2937', 
                    border: '2px dashed #7CD4FD',
                    color: '#E6EDF3'
                  }}
                >
                  <Upload className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                  <span>Favicon auswählen</span>
                  <input
                    type="file"
                    accept="image/x-icon,image/png"
                    onChange={handleFaviconChange}
                    className="hidden"
                  />
                </label>
                
                {faviconFile && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg" style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>
                    <Check className="w-5 h-5" />
                    <span>{faviconFile.name}</span>
                  </div>
                )}
              </div>
              <p className="text-sm mt-2" style={{ color: '#94A3B8' }}>
                Maximale Größe: 100KB. Unterstützte Formate: ICO, PNG
              </p>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                onClick={handleSubmit}
                disabled={uploading || (!logoFile && !faviconFile)}
                className="w-full sm:w-auto"
                style={{
                  backgroundColor: uploading || (!logoFile && !faviconFile) ? '#374151' : '#1EA0E6',
                  color: '#fff',
                  opacity: uploading || (!logoFile && !faviconFile) ? 0.5 : 1
                }}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Wird hochgeladen...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Branding speichern
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
